import { nextTick, onScopeDispose, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { watchDebounced } from "@vueuse/core";
import { handleNavigationFailure } from "@/shared/utils/navigation";
function readQueryString(value) {
    if (Array.isArray(value))
        value = value[0];
    return typeof value === "string" && value.length > 0 ? value : null;
}
const DEFAULT_PAGE_SIZE_FALLBACK = 10;
const DEFAULT_SEARCH_DEBOUNCE_MS = 300;
export function useTableQueryUrlSync(opts) {
    const { tableQuery, filterKeys = {}, extraParams = {}, syncView = false, syncSearch = false, searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS, defaultViewId, defaultPageSize = DEFAULT_PAGE_SIZE_FALLBACK, pageSizeOptions, } = opts;
    const router = useRouter();
    const route = useRoute();
    let isApplyingFromUrl = false;
    let rewriteAfterApply = false;
    // CONSTRAINT: the debounced search write outlives the component (its timer is not cleared on
    // unmount). Fired after the user left, `{ query }` would rewrite the NEXT page's query.
    let disposed = false;
    onScopeDispose(() => (disposed = true));
    function readPositiveInt(value) {
        const str = readQueryString(value);
        if (str === null)
            return null;
        const n = Number(str);
        return Number.isInteger(n) && n > 0 ? n : null;
    }
    function readPageSize(value) {
        const size = readPositiveInt(value);
        return size !== null && (!pageSizeOptions || pageSizeOptions.includes(size)) ? size : null;
    }
    function specParams(spec) {
        return [spec.paramName, ...(spec.ownedParams ?? [])];
    }
    function stateValues(filterKey) {
        const f = tableQuery.state.filters[filterKey];
        return f?.type === "in" ? f.values : [];
    }
    function statesMatch(query) {
        if (syncView) {
            const urlView = readQueryString(query.view) ?? defaultViewId ?? null;
            const stateView = tableQuery.state.activeViewId ?? defaultViewId ?? null;
            if (urlView !== stateView)
                return false;
        }
        if (syncSearch) {
            const urlSearch = readQueryString(query.q) ?? "";
            if (urlSearch !== tableQuery.state.search)
                return false;
        }
        const urlPage = readPositiveInt(query.page) ?? 1;
        if (urlPage !== tableQuery.state.page)
            return false;
        const urlPageSize = readPageSize(query.pageSize) ?? defaultPageSize;
        if (urlPageSize !== tableQuery.state.pageSize)
            return false;
        for (const [filterKey, spec] of Object.entries(filterKeys)) {
            if (spec.serialize) {
                const expected = spec.serialize(stateValues(filterKey));
                for (const param of specParams(spec)) {
                    const urlVal = readQueryString(query[param]);
                    if ((expected[param] ?? null) !== urlVal)
                        return false;
                }
            }
            else {
                const urlVal = readQueryString(query[spec.paramName]);
                const stateFilter = tableQuery.state.filters[filterKey];
                const stateVal = stateFilter?.type === "in" && stateFilter.values.length > 0
                    ? stateFilter.values.join(",")
                    : null;
                if (urlVal !== stateVal)
                    return false;
            }
        }
        for (const [param, spec] of Object.entries(extraParams)) {
            if (readQueryString(query[param]) !== spec.get())
                return false;
        }
        return true;
    }
    function applyUrlToState(query) {
        isApplyingFromUrl = true;
        try {
            if (syncView) {
                const urlView = readQueryString(query.view) ?? defaultViewId ?? null;
                if (urlView !== tableQuery.state.activeViewId) {
                    // setView mutates activeViewId and resets page; the consumer's own
                    // activeViewId watcher then applies preset filters synchronously.
                    tableQuery.setView(urlView);
                }
                else if (urlView !== null) {
                    // View id unchanged — Vue dedupes the mutation so the consumer's
                    // preset watcher won't re-fire. But filter params may have been
                    // removed from the URL (back/forward from a custom-filter override),
                    // and we need the preset's snapshot to be re-applied. Toggle through
                    // null to force the watcher to fire twice: first clears filters,
                    // then re-applies the preset's snapshot.
                    tableQuery.setView(null);
                    tableQuery.setView(urlView);
                }
            }
            // Override filters from URL when explicitly specified. Filters absent
            // from the URL stay as the preset watcher left them — for the
            // view-unchanged case, the toggle above ensured presets were re-applied.
            for (const [filterKey, spec] of Object.entries(filterKeys)) {
                if (spec.deserialize) {
                    const values = spec.deserialize(query);
                    if (values.length > 0) {
                        tableQuery.state.filters[filterKey] = { type: "in", values };
                    }
                }
                else {
                    const urlVal = readQueryString(query[spec.paramName]);
                    if (urlVal !== null) {
                        // CSV-encoded multi-value filters (e.g. status=Validated,Recorded); single values have no comma.
                        tableQuery.state.filters[filterKey] = { type: "in", values: urlVal.split(",") };
                    }
                }
            }
            if (syncSearch) {
                const urlSearch = readQueryString(query.q) ?? "";
                if (urlSearch !== tableQuery.state.search) {
                    tableQuery.state.search = urlSearch;
                }
            }
            const urlPage = readPositiveInt(query.page) ?? 1;
            if (urlPage !== tableQuery.state.page) {
                tableQuery.state.page = urlPage;
            }
            const urlPageSize = readPageSize(query.pageSize);
            const targetPageSize = urlPageSize ?? defaultPageSize;
            if (targetPageSize !== tableQuery.state.pageSize) {
                tableQuery.state.pageSize = targetPageSize;
            }
            // The URL keeps a refused page size until the state next moves: rewritten once applied.
            rewriteAfterApply = query.pageSize !== undefined && urlPageSize === null && pageSizeOptions !== undefined;
            for (const [param, spec] of Object.entries(extraParams)) {
                const urlVal = readQueryString(query[param]);
                if (urlVal !== spec.get())
                    spec.set(urlVal);
            }
        }
        finally {
            // CONSTRAINT: nextTick resolves after the whole flush, post watchers included, so the
            // state→URL watcher sees the guard and skips. Clear it earlier and the apply writes back.
            nextTick(() => {
                isApplyingFromUrl = false;
                if (rewriteAfterApply || extraParamsRefused() || tableStateCorrected()) {
                    rewriteAfterApply = false;
                    writeUrl(false);
                }
            });
        }
    }
    // A consumer may resolve an extra param's URL value to another one (an unknown site to the
    // default). Its change lands while the guard above holds, so the URL is rewritten once applied.
    // A null state is a value not resolved yet, never a refusal: rewriting then would drop the param.
    function extraParamsRefused() {
        return Object.entries(extraParams).some(([param, spec]) => {
            const value = spec.get();
            return value !== null && value !== readQueryString(route.query[param]);
        });
    }
    // A consumer's fallback (an unknown tab, a page past the last) runs inside the apply, while the
    // guard skips its write. Filters do not count as a correction: a preset view's filters are state
    // the URL omits.
    function tableStateCorrected() {
        if (syncView) {
            const urlView = readQueryString(route.query.view) ?? defaultViewId ?? null;
            if (urlView !== (tableQuery.state.activeViewId ?? defaultViewId ?? null))
                return true;
        }
        return (readPositiveInt(route.query.page) ?? 1) !== tableQuery.state.page;
    }
    function buildUrlQuery() {
        const query = { ...route.query };
        // Strip managed keys
        if (syncView)
            delete query.view;
        if (syncSearch)
            delete query.q;
        delete query.page;
        delete query.pageSize;
        for (const spec of Object.values(filterKeys)) {
            for (const param of specParams(spec)) {
                delete query[param];
            }
        }
        for (const param of Object.keys(extraParams))
            delete query[param];
        // Re-emit non-default values
        if (syncView) {
            const view = tableQuery.state.activeViewId;
            if (view && view !== defaultViewId)
                query.view = view;
        }
        if (syncSearch && tableQuery.state.search) {
            query.q = tableQuery.state.search;
        }
        if (tableQuery.state.page > 1)
            query.page = String(tableQuery.state.page);
        if (tableQuery.state.pageSize !== defaultPageSize) {
            query.pageSize = String(tableQuery.state.pageSize);
        }
        for (const [filterKey, spec] of Object.entries(filterKeys)) {
            if (spec.serialize) {
                for (const [param, value] of Object.entries(spec.serialize(stateValues(filterKey)))) {
                    if (value !== undefined)
                        query[param] = value;
                }
            }
            else {
                const filter = tableQuery.state.filters[filterKey];
                if (filter?.type === "in" && filter.values.length > 0) {
                    query[spec.paramName] = filter.values.join(",");
                }
            }
        }
        for (const [param, spec] of Object.entries(extraParams)) {
            const value = spec.get();
            if (value !== null)
                query[param] = value;
        }
        return query;
    }
    function writeUrl(usePush) {
        if (isApplyingFromUrl || disposed)
            return;
        const next = buildUrlQuery();
        const method = usePush ? router.push : router.replace;
        method.call(router, { query: next }).catch(handleNavigationFailure("useTableQueryUrlSync"));
    }
    // ── Hydrate state from URL on mount ─────────────────────────────────────────
    applyUrlToState(route.query);
    // ── State → URL ─────────────────────────────────────────────────────────────
    let replaceNextViewWrite = false;
    /**
     * Applies the screen's own correction of a value it cannot show (an unknown tab) without a history
     * entry, so Back never leads to the refused value. A view the user picks still gets one.
     */
    function withoutHistoryEntry(mutate) {
        replaceNextViewWrite = true;
        mutate();
        // The post-flush watcher has run by then; the flag must not outlive this correction, or the
        // user's next tab change would be written without a history entry.
        nextTick(() => (replaceNextViewWrite = false));
    }
    // CONSTRAINT: keep one watcher, so one navigation per flush. A view change resets the page in
    // the same flush, and a router call made before the previous one resolves cancels it
    // (NAVIGATION_CANCELLED): a separate page write would drop the view's push, and Back would skip
    // the page the view was picked from.
    watch(() => ({
        view: syncView ? tableQuery.state.activeViewId : null,
        filters: { ...tableQuery.state.filters },
        page: tableQuery.state.page,
        pageSize: tableQuery.state.pageSize,
        extras: Object.values(extraParams).map((spec) => spec.get()),
    }), (next, prev) => {
        const viewChanged = next.view !== prev.view;
        writeUrl(viewChanged && !replaceNextViewWrite);
    }, { deep: true, flush: "post" });
    if (syncSearch) {
        watchDebounced(() => tableQuery.state.search, () => writeUrl(false), { debounce: searchDebounceMs, flush: "post" });
    }
    // ── URL → State (browser back/forward, deep links) ─────────────────────────
    watch(() => route.query, (newQuery) => {
        if (isApplyingFromUrl)
            return;
        if (statesMatch(newQuery))
            return;
        applyUrlToState(newQuery);
    });
    return { withoutHistoryEntry };
}
